import { collection, query, onSnapshot, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { deleteDoc } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useLocation } from 'react-router-dom';

const sidebarStyle = (collapsed) => ({
  width: collapsed ? '48px' : '280px',
  backgroundColor: '#0f0f0f',
  color: '#fff',
  height: 'calc(100vh - 100px)',
  padding: collapsed ? '30px 8px' : '30px 20px',
  paddingRight: collapsed ? '8px' : '20px',
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  right: 0,
  top: '64px',
  boxShadow: '-2px 0 6px rgba(0,0,0,0.1)',
  transition: 'width 0.3s ease',
  overflowY: 'auto', // ✅ add this
  overflowX: 'hidden',
  zIndex: 1000,
});

const toggleButtonStyle = {
  backgroundColor: 'transparent',
  color: '#FFD700',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  alignSelf: 'flex-end',
  marginBottom: '20px',
};

const searchInputStyle = {
  width: '93%',
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#1f1f1f',
  color: '#fff',
  marginBottom: '16px',
  fontSize: '14px',
};

const sectionHeader = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#e5e7eb',
  margin: '20px 0 10px 0',
};

const baseThreadStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: `'Helvetica Neue', sans-serif`,
  cursor: 'pointer',
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listItem = {
  ...baseThreadStyle,
  backgroundColor: '#1f1f1f',
  color: '#fff',
  fontWeight: '500',
};

const activeThreadStyle = {
  ...baseThreadStyle,
  backgroundColor: '#FFD700',
  color: '#000',
  fontWeight: '550', // same as listItem now
};

const menuButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#ccc',
  cursor: 'pointer',
  fontSize: '18px',
  marginLeft: '8px',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: '30px',
  right: '0px',
  backgroundColor: '#1f1f1f',
  border: '1px solid #333',
  borderRadius: '8px',
  padding: '6px 0',
  zIndex: 1001,
  minWidth: '140px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
};

const dropdownItemStyle = {
  padding: '8px 12px',
  color: '#fff',
  fontSize: '13px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const dropdownIconStyle = {
  float: 'right',
  cursor: 'pointer',
  color: '#FFD700',
  fontSize: '16px',
};

const ThreadSidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [threads, setThreads] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [userUid, setUserUid] = useState(null);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [dropdownThreadId, setDropdownThreadId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const currentThreadId = location.pathname.startsWith('/thread/') ? location.pathname.split('/thread/')[1] : null;
  const prevThreadCountRef = useRef(0);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) setUserUid(user.uid);
      else setUserUid(null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
  }, [currentThreadId]);

  useEffect(() => {
    if (!userUid) return;
    const threadRef = collection(db, 'users', userUid, 'threads');
    const q = query(threadRef);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const threadData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const posA = a.position ?? 9999;
          const posB = b.position ?? 9999;

          if (posA !== posB) return posA - posB;

          const createdA = a.createdAt?.seconds ?? 0;
          const createdB = b.createdAt?.seconds ?? 0;

          if (createdA !== createdB) return createdA - createdB;

          return a.id.localeCompare(b.id); // Tie-breaker
        });

      setThreads(threadData);

      // 🔁 Update the previous count ref after processing
      prevThreadCountRef.current = threadData.length;
    });
    return () => unsubscribe();
  }, [userUid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownThreadId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRename = async (id, newName) => {
    if (!newName.trim()) return;
    const docRef = doc(db, 'users', userUid, 'threads', id);
    await updateDoc(docRef, { name: newName, lastEdited: new Date() });
    setEditingId(null);
  };

  const handlePinToggle = async (id, current) => {
    const docRef = doc(db, 'users', userUid, 'threads', id);
    await updateDoc(docRef, { pinned: !current });
    setMenuOpenId(null);
  };

  const handleDelete = async (id) => {
    const docRef = doc(db, 'users', userUid, 'threads', id);
    await deleteDoc(docRef);
    setDropdownThreadId(null);
  };

  const getSmartThreadTitle = (t) => {
    if (t.name && t.name.trim()) return t.name;

    switch (t.type) {
      case 'prospecting':
        return `Prospecting – ${t.metadata?.persona || 'Persona'} – ${t.metadata?.industry || 'Industry'}`;
      case 'prep':
        return `Prep – ${t.metadata?.company || 'Company'}`;
      case 'postcall':
        return `Post-Call – ${t.metadata?.company || 'Company'}`;
      case 'industry':
        return `Industry Briefing – ${t.metadata?.industry || 'Industry'}`;
      default:
        return `Thread ${t.id.slice(0, 6)}`;
    }
  };

  const renderThread = (t) => (
    <div
      key={t.id}
      style={t.id === currentThreadId ? activeThreadStyle : listItem}
    >
      {editingId === t.id ? (
        <input
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={() => handleRename(t.id, editText)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRename(t.id, editText);
          }}
          autoFocus
          style={{
            width: '70%',
            padding: '4px 6px',
            fontSize: '13px',
            borderRadius: '4px',
            border: '1px solid #555',
            backgroundColor: '#1f1f1f',
            color: '#fff',
          }}
        />
      ) : (
        <div
          onClick={() => {
            navigate(`/thread/${t.id}`);
          }}
          style={{ flex: 1 }}
        >
          {getSmartThreadTitle(t)}
        </div>
      )}

      <button
        style={menuButtonStyle}
        onClick={() => {
          const newId = menuOpenId === t.id ? null : t.id;
          setMenuOpenId(newId);
          setDropdownThreadId(newId);
        }}
      >
        ⋮
      </button>

      {menuOpenId === t.id && (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          {dropdownThreadId === t.id && (
            <div style={dropdownMenuStyle} className="dropdown-thread-menu">
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', color: '#fff' }}
                onClick={() => {
                  setEditingId(t.id);
                  setEditText(t.name || '');
                  setDropdownThreadId(null);
                }}
              >
                Rename
              </div>
              <div
                style={dropdownItemStyle}
                onClick={() => {
                  handlePinToggle(t.id, t.pinned);
                  setMenuOpenId(null);
                }}
              >
                {t.pinned ? 'Unpin from Sidebar' : 'Pin to Sidebar'}
              </div>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', color: '#ff4d4d' }}
                onClick={() => {
                  handleDelete(t.id);
                  setDropdownThreadId(null);
                }}
              >
                Delete
              </div>
            </div>
          )}
        </div>

      )}
    </div>
  );

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || !userUid) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const movedThread = threads.find(t => t.id === draggableId);
    if (!movedThread) return;

    const getList = (zone) => {
      return threads.filter((t) =>
        zone === 'pinned' ? t.pinned : !t.pinned
      );
    };

    let sourceList = getList(source.droppableId).slice();
    let destList = getList(destination.droppableId).slice();

    const [movedItem] = sourceList.splice(source.index, 1);
    destList.splice(destination.index, 0, movedItem);

    const updates = new Map();

    const applyUpdates = (list, droppableId) => {
      list.forEach((t, i) => {
        const data = {
          position: i,
          pinned: droppableId === 'pinned',
        };
        updates.set(t.id, data);
      });
    };

    applyUpdates(destList, destination.droppableId);
    if (source.droppableId !== destination.droppableId) {
      applyUpdates(sourceList, source.droppableId);
    }

    try {
      const updatePromises = Array.from(updates.entries()).map(([id, data]) => {
        console.log(`📌 Updating ${id} → position: ${data.position}, pinned: ${data.pinned}`);
        return updateDoc(doc(db, 'users', userUid, 'threads', id), data);
      });

      await Promise.all(updatePromises);
      console.log('✅ Reorder successful.');
    } catch (err) {
      console.error('❌ Firestore update error:', err);
    }
  };


  console.log('📦 All threads:', threads);

  const filtered = threads.filter(t =>
    (t.name || '').toLowerCase().includes(searchQuery)
  );

  const pinnedThreads = filtered
    .filter(t => t.pinned)
    .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));

  const recentThreads = filtered
    .filter(t => !t.pinned)
    .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));

  console.log('📦 Filtered + Pinned:', pinnedThreads);
  console.log('📦 Filtered + Recent:', recentThreads);

  return (
    <div style={sidebarStyle(collapsed)}>
      <button style={toggleButtonStyle} onClick={() => setCollapsed(!collapsed)}>
        {collapsed ? '◀' : '▶'}
      </button>

      {!collapsed && (
        <>
          <input
            style={searchInputStyle}
            placeholder="Search Threads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          />
          <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <div style={sectionHeader}>Pinned</div>
              <Droppable droppableId="pinned">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      minHeight: '40px',
                      paddingBottom: '4px',
                      borderRadius: '6px',
                      backgroundColor: snapshot.isDraggingOver ? '#1a1a1a' : 'transparent',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {pinnedThreads.map((t, index) => (
                      <Draggable key={t.id} draggableId={t.id} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              {renderThread(t)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              <div style={sectionHeader}>Recent</div>
              <Droppable droppableId="recent">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      minHeight: '40px',
                      paddingBottom: '4px',
                      borderRadius: '6px',
                      backgroundColor: snapshot.isDraggingOver ? '#1a1a1a' : 'transparent',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {recentThreads.map((t, index) => (
                      <Draggable key={t.id} draggableId={t.id} index={index}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              {renderThread(t)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

        </>
      )}
    </div>
  );
};

export default ThreadSidebar;